import type { SimulationState, ThemeData } from '../types';
import type { GameEvent } from '../events/GameEvents';
import type { SeededRandom } from '../rng/SeededRandom';

function xpToNextLevel(level: number, themeData: ThemeData): number {
  return Math.floor(themeData.balance.xpBase * Math.pow(themeData.balance.xpGrowthRate, level));
}

function getXPMultiplier(staff: { traits: string[] }, themeData: ThemeData, prestigeXPMultiplier: number): number {
  let multiplier = prestigeXPMultiplier;
  for (const traitId of staff.traits) {
    const traitDef = themeData.entities.traits.find((t) => t.id === traitId);
    if (!traitDef) continue;
    for (const effect of traitDef.effects) {
      if (effect.type === 'XP_MODIFIER') {
        multiplier *= effect.multiplier;
      }
    }
  }
  return multiplier;
}

export function levelingSystemUpdate(
  state: SimulationState,
  themeData: ThemeData,
  _rng: SeededRandom,
): GameEvent[] {
  const events: GameEvent[] = [];
  const prestigeXPMult = state.company.prestigeBonuses.xpMultiplier;

  for (const staff of state.staff) {
    if (staff.isTraining || staff.isOnLeave) continue;
    if (staff.level >= themeData.balance.maxStaffLevel) continue;

    // Staff earn XP when working on a project
    const isWorking = staff.teamId
      ? state.teams.some(
          (t) => t.id === staff.teamId && t.assignedProjectId !== null,
        )
      : false;

    // Also earn XP on contracts
    const isOnContract = state.contracts.some(
      (c) => c.isActive && c.assignedStaffIds.includes(staff.id),
    );

    if (!isWorking && !isOnContract) continue;

    const baseXP = 1;
    const xpMult = getXPMultiplier(staff, themeData, prestigeXPMult);
    const xpGain = baseXP * xpMult;

    staff.xp += xpGain;

    // Check for level up
    while (staff.xp >= staff.xpToNextLevel && staff.level < themeData.balance.maxStaffLevel) {
      staff.xp -= staff.xpToNextLevel;
      staff.level++;
      staff.xpToNextLevel = xpToNextLevel(staff.level, themeData);

      // Stat gains: 1 + floor(level / 5) per stat
      const statGain = 1 + Math.floor(staff.level / 5);
      const statGains: Record<string, number> = {};
      for (const stat of Object.keys(staff.stats)) {
        staff.stats[stat] = (staff.stats[stat] ?? 0) + statGain;
        statGains[stat] = statGain;
      }

      // Salary expectation rises: +5% per level
      staff.desiredSalary = Math.round(staff.desiredSalary * 1.05);

      events.push({
        type: 'STAFF_LEVELED_UP',
        staffId: staff.id,
        name: staff.name,
        newLevel: staff.level,
        statGains,
      });
    }
  }

  return events;
}
